// FieldVision - MarkerRow.tsx
// Enkelt mark-raekke i marker-listen
// FIX Issue 1: Viser blaa highlight for zoom-loaded marker
// FIX Issue 2: Viser kort-antal som badge med tal

import React, { useMemo } from 'react';

interface Field {
  Id: number;
  Marknr: string;
  CVR: string;
  IMK_areal: number;
  Geometry?: string;
  isZoomLoaded?: boolean;
}

interface MarkWithMaps extends Field {
  maps: {
    hasNDVI: boolean;
    hasUkrudt: boolean;
    totalMaps: number;
  };
}

interface MarkerRowProps {
  field: MarkWithMaps;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isSelected?: boolean;
  loadingMaps?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

// Render polygon thumbnail baseret paa faktisk geometry
const renderThumbnail = (geometry: string | undefined, isZoomLoaded?: boolean) => {
  const fillColor = isZoomLoaded ? '#a0532a' : '#4783b6';
  const strokeColor = isZoomLoaded ? '#a0532a' : '#4783b6';

  if (!geometry) {
    return (
      <svg viewBox="0 0 40 32" className="thumb-svg">
        <polygon
          points="20,2 38,10 38,24 20,30 2,24 2,10"
          fill={fillColor}
          fillOpacity="0.6"
          stroke={strokeColor}
          strokeWidth="2"
        />
      </svg>
    );
  }

  try {
    const geo = JSON.parse(geometry);

    let coords: number[][];
    if ((geo as any).type === 'MultiPolygon') {
      coords = (geo as any).coordinates[0][0];
    } else if ((geo as any).type === 'Polygon') {
      coords = (geo as any).coordinates[0];
    } else {
      return (
        <svg viewBox="0 0 40 32" className="thumb-svg">
          <polygon
            points="20,2 38,10 38,24 20,30 2,24 2,10"
            fill={fillColor}
            fillOpacity="0.6"
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    }

    if (!coords || coords.length < 3) {
      return (
        <svg viewBox="0 0 40 32" className="thumb-svg">
          <polygon
            points="20,2 38,10 38,24 20,30 2,24 2,10"
            fill={fillColor}
            fillOpacity="0.6"
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    }

    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    for (const coord of coords) {
      if (Array.isArray(coord) && coord.length >= 2) {
        const lon = coord[0];
        const lat = coord[1];
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      }
    }

    const width = maxLon - minLon;
    const height = maxLat - minLat;

    if (width === 0 || height === 0) {
      return (
        <svg viewBox="0 0 40 32" className="thumb-svg">
          <polygon
            points="20,2 38,10 38,24 20,30 2,24 2,10"
            fill={fillColor}
            fillOpacity="0.6"
            stroke={strokeColor}
            strokeWidth="2"
          />
        </svg>
      );
    }

    const svgWidth = 40;
    const svgHeight = 32;
    const padding = 2;
    const availWidth = svgWidth - (padding * 2);
    const availHeight = svgHeight - (padding * 2);

    const scaleX = availWidth / width;
    const scaleY = availHeight / height;
    const scale = Math.min(scaleX, scaleY);

    const scaledWidth = width * scale;
    const scaledHeight = height * scale;
    const offsetX = padding + (availWidth - scaledWidth) / 2;
    const offsetY = padding + (availHeight - scaledHeight) / 2;

    const points = coords
      .filter((c: any) => Array.isArray(c) && c.length >= 2)
      .map((c: any) => {
        const x = offsetX + (c[0] - minLon) * scale;
        const y = offsetY + (maxLat - c[1]) * scale;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

    return (
      <svg viewBox="0 0 40 32" className="thumb-svg">
        <polygon
          points={points}
          fill={fillColor}
          fillOpacity="0.6"
          stroke={strokeColor}
          strokeWidth="1.5"
        />
      </svg>
    );
  } catch (e) {
    return (
      <svg viewBox="0 0 40 32" className="thumb-svg">
        <polygon
          points="20,2 38,10 38,24 20,30 2,24 2,10"
          fill={fillColor}
          fillOpacity="0.6"
          stroke={strokeColor}
          strokeWidth="2"
        />
      </svg>
    );
  }
};

const MarkerRow: React.FC<MarkerRowProps> = ({
  field,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isSelected = false,
  loadingMaps = false,
  onEdit
}) => {
  const thumbnail = useMemo(
    () => renderThumbnail((field as any).Geometry, (field as any).isZoomLoaded),
    [(field as any).Geometry, (field as any).isZoomLoaded]
  );

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  return (
    <div
      className={`marker-row ${isSelected ? 'selected' : ''} ${(field as any).isZoomLoaded ? 'zoom-loaded' : ''}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Thumbnail */}
      <div className="marker-thumb">
        {thumbnail}
      </div>

      {/* Mark info */}
      <div className="marker-info">
        <span className="marker-name">{(field as any).Marknr || 'Uden navn'}</span>
        <span className="marker-areal">
          {((field as any).IMK_areal as number)?.toFixed(1) || '0.0'} ha
        </span>
      </div>

      {/* FIX Issue 2: Kort-antal med tal */}
      <div className="kort-info">
        {loadingMaps ? (
          <span className="loading-dot">...</span>
        ) : (
          <span className={`kort-count ${(field as any).maps?.totalMaps > 0 ? 'has-maps' : 'no-maps'}`}>
            {(field as any).maps?.totalMaps || 0}
          </span>
        )}
      </div>

      {/* Ret knap */}
      {onEdit && (
        <button
          className="action-btn edit-btn"
          onClick={handleEditClick}
          title="Rediger mark"
        >
          ✏️
        </button>
      )}

      {/* Slet knap - SKJULT (kommer i fremtidig version) */}

      <style>{`
        .marker-row {
          display: grid;
          grid-template-columns: 48px 1fr 60px;
          gap: 8px;
          padding: 12px 20px;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          align-items: center;
          transition: background 0.15s;
        }

        .marker-row:hover {
          background: #f0fdf4;
        }

        .marker-row.selected {
          background: #dcfce7;
          border-left: 3px solid #16a34a;
          padding-left: 17px;
        }

        /* FIX Issue 1: Zoom-loaded marker har blaa highlight */
        .marker-row.zoom-loaded {
          border-left: 3px solid #3b82f6;
          padding-left: 17px;
        }

        .marker-row.zoom-loaded:hover {
          background: #eff6ff;
        }

        .marker-row.zoom-loaded.selected {
          background: #dbeafe;
          border-left-color: #2563eb;
        }

        .marker-thumb {
          width: 40px;
          height: 32px;
        }

        .thumb-svg {
          width: 100%;
          height: 100%;
        }

        .marker-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .marker-name {
          font-weight: 600;
          font-size: 14px;
          color: #111827;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .marker-areal {
          font-size: 13px;
          color: #16a34a;
          font-weight: 500;
        }

        .zoom-loaded .marker-areal {
          color: #3b82f6;
        }

        /* FIX Issue 2: Kort info med antal */
        .kort-info {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .kort-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 6px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;
        }

        .kort-count.has-maps {
          background: #16a34a;
          color: white;
        }

        .kort-count.no-maps {
          background: #f3f4f6;
          color: #9ca3af;
        }

        .loading-dot {
          color: #9ca3af;
          font-size: 12px;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          opacity: 0.5;
          transition: all 0.15s;
        }

        .action-btn:hover {
          opacity: 1;
          background: #f3f4f6;
        }

        .edit-btn:hover {
          background: #dbeafe;
        }
      `}</style>
    </div>
  );
};

export default MarkerRow;
